import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../../components/ui/Button";
import { Dialog } from "../../components/ui/Dialog";
import { DocMarks, Spinner } from "../../components/ui/misc";
import { toast } from "../../components/ui/Toast";
import { api } from "../../lib/api/endpoints";
import type { Space } from "../../lib/api/models";
import { formatDate } from "../../lib/format";
import { qk } from "../../lib/queries";

export function ArchivedDialog({ space, open, onClose }: { space: Space; open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["archived", space.id],
    queryFn: () => api.archivedTree(space.id),
    enabled: open,
  });
  const restore = async (id: string, title: string) => {
    try {
      await api.documents.unarchive(id);
      toast(`“${title}” restored.`, "success");
      await refetch();
      await queryClient.invalidateQueries({ queryKey: qk.tree(space.id) });
    } catch (e) {
      toast(`Restore failed: ${e instanceof Error ? e.message : "unknown error"}`, "error");
    }
  };
  return (
    <Dialog open={open} onClose={onClose} title={`Archived in ${space.name}`}>
      {isLoading ? <Spinner label="Loading…" /> : null}
      {data && data.length === 0 ? <p className="text-sm text-ink-3">Nothing archived here.</p> : null}
      <ul className="grid gap-1">
        {(data ?? []).map((d) => (
          <li key={d.id} className="flex items-center gap-2 rounded-md bg-column px-3 py-1.5 text-sm">
            <span className="flex-1">{d.title}</span>
            <DocMarks code={d.code} kind={d.kind} />
            <span className="text-xs text-ink-3">{formatDate(d.archivedAt)}</span>
            <Button size="sm" aria-label={`Restore ${d.title}`} onClick={() => void restore(d.id, d.title)}>
              Restore
            </Button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}
